import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Mic, MicOff, Send, Volume2, VolumeX, User, Bot, Loader2, Phone, Mail, MessageSquare, X, PhoneOff, PhoneCall } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Voice conversation states
const VOICE_STATE = {
  IDLE: 'idle',
  LISTENING: 'listening',
  THINKING: 'thinking',
  SPEAKING: 'speaking',
};

// ==================== ANIMATED WAVEFORM ====================

const WaveformVisualizer = ({ state, color, size = 'lg' }) => {
  const bars = size === 'lg' ? 32 : 20;
  const barH = size === 'lg' ? 64 : 32;
  const isActive = state === VOICE_STATE.LISTENING || state === VOICE_STATE.SPEAKING;
  const isSpeaking = state === VOICE_STATE.SPEAKING;

  return (
    <div className={cn("flex items-center justify-center gap-[3px]", size === 'lg' ? 'h-20' : 'h-8')} data-testid="waveform-bars">
      {Array.from({ length: bars }).map((_, i) => {
        const centerDist = Math.abs(i - bars / 2) / (bars / 2);
        const maxH = barH * (1 - centerDist * 0.5);
        return (
          <div
            key={i}
            className="rounded-full transition-all"
            style={{
              width: size === 'lg' ? '3px' : '2px',
              backgroundColor: isSpeaking ? color : (state === VOICE_STATE.LISTENING ? '#EF4444' : color),
              height: isActive ? `${Math.max(6, maxH * 0.3)}px` : '4px',
              opacity: isActive ? 0.6 + Math.random() * 0.4 : 0.2,
              animation: isActive ? `waveBar ${0.3 + Math.random() * 0.5}s ease-in-out infinite alternate` : 'none',
              animationDelay: `${i * 0.025}s`,
            }}
          />
        );
      })}
    </div>
  );
};

// ==================== LEAD CAPTURE FORM ====================

const LeadCaptureForm = ({ config, onSubmit, onSkip }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const fields = config?.lead_capture_fields || {};

  return (
    <div className="p-6 rounded-2xl bg-white/90 backdrop-blur-md shadow-lg max-w-sm w-full mx-auto" data-testid="lead-capture-form">
      <h3 className="text-lg font-semibold mb-1" style={{ color: config?.secondary_color }}>
        Before we chat
      </h3>
      <p className="text-sm text-gray-500 mb-4">Let us know how to reach you</p>
      <div className="space-y-3">
        {fields.name !== false && (
          <input type="text" placeholder="Your name" value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2"
            data-testid="lead-name-input" />
        )}
        {fields.email !== false && (
          <input type="email" placeholder="Email address" value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2"
            data-testid="lead-email-input" />
        )}
        {fields.phone && (
          <input type="tel" placeholder="Phone number" value={form.phone}
            onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2"
            data-testid="lead-phone-input" />
        )}
        {fields.company && (
          <input type="text" placeholder="Company" value={form.company}
            onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2"
            data-testid="lead-company-input" />
        )}
        <button onClick={() => onSubmit(form)}
          className="w-full py-2.5 rounded-lg text-white text-sm font-medium transition-opacity hover:opacity-90"
          style={{ backgroundColor: config?.primary_color }}
          data-testid="lead-submit-btn">
          Start Conversation
        </button>
        {!config?.require_contact_before_chat && (
          <button onClick={onSkip}
            className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="lead-skip-btn">
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
};

// ==================== CHAT BUBBLE (for text mode) ====================

const ChatBubble = ({ msg, config }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={cn("flex gap-2 max-w-[85%]", isUser ? "ml-auto flex-row-reverse" : "")} data-testid={`chat-msg-${msg.role}`}>
      <div className={cn("w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5", isUser ? "bg-gray-200" : "")}
        style={!isUser ? { backgroundColor: config?.primary_color + '20' } : {}}>
        {isUser ? <User className="h-3.5 w-3.5 text-gray-600" /> : <Bot className="h-3.5 w-3.5" style={{ color: config?.primary_color }} />}
      </div>
      <div className={cn("rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
        isUser ? "bg-gray-100 text-gray-800 rounded-tr-sm" : "text-white rounded-tl-sm"
      )} style={!isUser ? { backgroundColor: config?.primary_color } : {}}>
        <p className="whitespace-pre-wrap">{msg.content}</p>
      </div>
    </div>
  );
};


// ==================== MAIN PUBLIC VOICE PAGE ====================

export default function PublicVoicePage() {
  const { tenantSlug } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [voiceState, setVoiceState] = useState(VOICE_STATE.IDLE);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [inCall, setInCall] = useState(false); // true = voice call mode active
  const [chatMode, setChatMode] = useState(false); // true = text chat mode
  const [liveTranscript, setLiveTranscript] = useState(''); // what's being said right now
  const [lastBotText, setLastBotText] = useState(''); // last thing the AI said

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const autoListenRef = useRef(false);
  // Refs to avoid stale closures in Speech Recognition callbacks
  const sessionTokenRef = useRef(null);
  const sendingRef = useRef(false);
  const configRef = useRef(null);
  const ttsEnabledRef = useRef(true);

  // Keep refs in sync with state (avoids stale closures in recognition/audio callbacks)
  useEffect(() => { sessionTokenRef.current = sessionToken; }, [sessionToken]);
  useEffect(() => { sendingRef.current = sending; }, [sending]);
  useEffect(() => { configRef.current = config; }, [config]);
  useEffect(() => { ttsEnabledRef.current = ttsEnabled; }, [ttsEnabled]);

  // Load config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await fetch(`${API}/api/voice/public/config/${tenantSlug}`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setConfig(data);
        if (data.require_contact_before_chat && data.lead_capture_enabled) {
          setShowLeadForm(true);
        }
        // Default to chat mode if voice-only is off
        if (data.conversation_mode === 'chat_only') {
          setChatMode(true);
        }
      } catch {
        setError('This voice receptionist is not available.');
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
    // Preload browser voices (they load async in Chrome)
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    }
  }, [tenantSlug]);

  // Auto-scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ---- Core conversation functions ----

  const startSession = useCallback(async (leadData = {}) => {
    // Retry up to 3 times in case of transient failures
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await fetch(`${API}/api/voice/public/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_slug: tenantSlug,
            visitor_name: leadData.name || '',
            visitor_email: leadData.email || '',
            visitor_phone: leadData.phone || '',
          }),
        });
        const data = await res.json();
        setSessionToken(data.session_token);
        sessionTokenRef.current = data.session_token;
        setShowLeadForm(false);

        const greeting = data.greeting || config?.greeting_message || `Hi! I'm ${config?.receptionist_name || 'Aria'}. How can I help you today?`;
        setMessages([{ role: 'assistant', content: greeting }]);
        setLastBotText(greeting);
        return { token: data.session_token, greeting };
      } catch (err) {
        console.error(`Session start attempt ${attempt + 1} failed:`, err);
        if (attempt < 2) await new Promise(r => setTimeout(r, 500));
      }
    }
    return null;
  }, [tenantSlug, config]);

  // Auto-start session if no lead form
  useEffect(() => {
    if (config && !showLeadForm && !sessionToken) {
      startSession();
    }
  }, [config, showLeadForm, sessionToken, startSession]);

  const sendMessage = async (text, token) => {
    const msg = text.trim();
    const sToken = token || sessionTokenRef.current;
    if (!msg || !sToken) return null;

    setMessages(prev => [...prev, { role: 'user', content: msg }]);

    // ── SNAPPY: instant filler so user hears something within ~50ms ──
    // Pure browser SpeechSynthesis — zero network. Plays ONLY if TTS is on
    // AND the user actually expects voice (we're in a voice session).
    if (ttsEnabledRef.current && typeof window !== 'undefined' && window.speechSynthesis && !window.speechSynthesis.speaking) {
      try {
        const FILLERS = ['One moment.', 'Let me check that.', 'Got it, one second.', 'Okay, looking that up.'];
        const filler = FILLERS[Math.floor(Math.random() * FILLERS.length)];
        const u = new SpeechSynthesisUtterance(filler);
        u.rate = 1.1; u.pitch = 1.0; u.volume = 0.85;
        const voices = window.speechSynthesis.getVoices();
        const preferred = voices.find(v => v.lang === 'en-US' && v.name.toLowerCase().includes('female')) || voices.find(v => v.lang.startsWith('en'));
        if (preferred) u.voice = preferred;
        window.speechSynthesis.speak(u);
      } catch { /* ignore */ }
    }

    try {
      // SNAPPY combined endpoint — chat + first-sentence TTS in ONE round-trip
      const res = await fetch(`${API}/api/voice/public/chat-and-speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_token: sToken,
          message: msg,
          voice_id: configRef.current?.voice_id || 'rachel',
        }),
      });
      const data = await res.json();
      if (data?.answer) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);
        setLastBotText(data.answer);
        // If the server gave us audio for the first sentence, attach it for playTTS to use
        if (data.first_audio_b64) {
          window.__VOICE_FIRST_AUDIO_B64 = data.first_audio_b64;
        } else {
          window.__VOICE_FIRST_AUDIO_B64 = null;
        }
        return data.answer;
      }
    } catch {
      // Fallback to the legacy endpoint
      try {
        const res2 = await fetch(`${API}/api/voice/public/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_token: sToken, message: msg }),
        });
        const d2 = await res2.json();
        if (d2?.answer) {
          setMessages(prev => [...prev, { role: 'assistant', content: d2.answer }]);
          setLastBotText(d2.answer);
          return d2.answer;
        }
      } catch { /* ignore */ }
      const fallback = "Sorry, I'm having trouble right now. Please try again.";
      setMessages(prev => [...prev, { role: 'assistant', content: fallback }]);
      return fallback;
    }
    return null;
  };

  // After speech finishes (either ElevenLabs or browser), auto-listen if in call
  const onSpeechDone = useCallback(() => {
    setVoiceState(VOICE_STATE.IDLE);
    if (autoListenRef.current && recognitionRef.current) {
      setTimeout(() => {
        try {
          recognitionRef.current.start();
          setVoiceState(VOICE_STATE.LISTENING);
        } catch { /* already started */ }
      }, 150);
    }
  }, []);

  // Split text into speakable sentences
  const splitSentences = useCallback((text) => {
    // Split on sentence boundaries but keep short phrases together
    const raw = text.match(/[^.!?]+[.!?]+\s*/g) || [text];
    return raw.map(s => s.trim()).filter(s => s.length > 0);
  }, []);

  // Sentence-by-sentence browser TTS for near-instant perceived response
  const speakSentenceQueue = useRef([]);
  const isSpeakingQueue = useRef(false);

  const speakNextSentence = useCallback(() => {
    if (speakSentenceQueue.current.length === 0) {
      isSpeakingQueue.current = false;
      onSpeechDone();
      return;
    }
    const sentence = speakSentenceQueue.current.shift();
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      speakNextSentence();
      return;
    }
    const utterance = new SpeechSynthesisUtterance(sentence);
    utterance.rate = 1.05;
    utterance.pitch = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => v.name.includes('Samantha') || v.name.includes('Google US English') || v.name.includes('Microsoft Zira') || (v.lang === 'en-US' && v.name.toLowerCase().includes('female')));
    if (preferred) utterance.voice = preferred;
    else {
      const enVoice = voices.find(v => v.lang.startsWith('en'));
      if (enVoice) utterance.voice = enVoice;
    }
    utterance.onend = speakNextSentence;
    utterance.onerror = speakNextSentence;
    window.speechSynthesis.speak(utterance);
  }, [onSpeechDone]);

  const playStreamingTTS = useCallback((text) => {
    if (!text || !ttsEnabledRef.current) return;
    setVoiceState(VOICE_STATE.SPEAKING);
    // Cancel anything in progress
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    const sentences = splitSentences(text);
    speakSentenceQueue.current = sentences;
    isSpeakingQueue.current = true;
    speakNextSentence();
  }, [splitSentences, speakNextSentence]);

  // Browser-native fallback TTS (free, always available)
  const playBrowserTTS = useCallback((text) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      onSpeechDone();
      return;
    }
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.05;
    utterance.pitch = 1.0;
    // Try to pick a natural-sounding voice
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => v.name.includes('Samantha') || v.name.includes('Google US English') || v.name.includes('Microsoft Zira') || (v.lang === 'en-US' && v.name.toLowerCase().includes('female')));
    if (preferred) utterance.voice = preferred;
    else if (voices.length > 0) {
      const enVoice = voices.find(v => v.lang.startsWith('en'));
      if (enVoice) utterance.voice = enVoice;
    }
    utterance.onend = onSpeechDone;
    utterance.onerror = onSpeechDone;
    window.speechSynthesis.speak(utterance);
  }, [onSpeechDone]);

  // Main TTS: Use sentence-streaming browser TTS for instant response.
  // If sendMessage prefetched the first sentence's audio (combined endpoint),
  // we play that instantly — saving an entire HTTP round-trip.
  const playTTSRef = useRef(null);
  const playTTS = useCallback(async (text) => {
    if (!text || !ttsEnabledRef.current) return;
    setVoiceState(VOICE_STATE.SPEAKING);

    // Cancel the "thinking filler" if it's still playing
    if (typeof window !== 'undefined' && window.speechSynthesis && window.speechSynthesis.speaking) {
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    }

    const sentences = splitSentences(text);
    if (sentences.length === 0) { onSpeechDone(); return; }

    // ── SNAPPY: play the audio that came back WITH the chat response ──
    const prefetched = (typeof window !== 'undefined') ? window.__VOICE_FIRST_AUDIO_B64 : null;
    if (prefetched) {
      try {
        window.__VOICE_FIRST_AUDIO_B64 = null; // consume once
        if (audioRef.current) audioRef.current.pause();
        const audio = new Audio(`data:audio/mp3;base64,${prefetched}`);
        audioRef.current = audio;
        audio.onended = () => {
          if (sentences.length > 1) {
            speakSentenceQueue.current = sentences.slice(1);
            isSpeakingQueue.current = true;
            speakNextSentence();
          } else {
            onSpeechDone();
          }
        };
        audio.onerror = () => { playStreamingTTS(text); };
        await audio.play();
        return;
      } catch {
        /* fall through */
      }
    }

    // Legacy path: separate TTS round-trip for first sentence
    try {
      const firstSentence = sentences[0];
      const res = await fetch(`${API}/api/voice/public/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: firstSentence, voice_id: configRef.current?.voice_id || 'rachel' }),
      });
      if (res.ok && res.headers.get('content-type')?.includes('audio')) {
        const blob = await res.blob();
        const audioUrl = URL.createObjectURL(blob);
        if (audioRef.current) audioRef.current.pause();
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          if (sentences.length > 1) {
            speakSentenceQueue.current = sentences.slice(1);
            isSpeakingQueue.current = true;
            speakNextSentence();
          } else {
            onSpeechDone();
          }
        };
        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          playStreamingTTS(text);
        };
        audio.play().catch(() => {
          URL.revokeObjectURL(audioUrl);
          playStreamingTTS(text);
        });
        return;
      }
    } catch {
      // Kaharian TTS failed — fall through to streaming browser TTS
    }

    // Default: sentence-by-sentence browser TTS (zero latency)
    playStreamingTTS(text);
  }, [onSpeechDone, splitSentences, playStreamingTTS, speakNextSentence]);
  // Keep ref current
  useEffect(() => { playTTSRef.current = playTTS; }, [playTTS]);

  // Handle voice input → send → TTS response (the full call loop)
  // Uses refs to always have the latest state, avoiding stale closures
  const handleVoiceSend = useCallback(async (transcript) => {
    if (!transcript.trim() || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setVoiceState(VOICE_STATE.THINKING);

    const answer = await sendMessage(transcript);
    sendingRef.current = false;
    setSending(false);

    if (answer && ttsEnabledRef.current) {
      await playTTSRef.current?.(answer);
    } else {
      setVoiceState(VOICE_STATE.IDLE);
      if (autoListenRef.current && recognitionRef.current) {
        setTimeout(() => {
          try {
            recognitionRef.current.start();
            setVoiceState(VOICE_STATE.LISTENING);
          } catch { /* already started */ }
        }, 150);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep handleVoiceSend accessible via ref for recognition callback
  const handleVoiceSendRef = useRef(null);
  useEffect(() => { handleVoiceSendRef.current = handleVoiceSend; }, [handleVoiceSend]);

  // Setup Web Speech API — runs once, uses refs for all mutable state
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += t;
        } else {
          interimTranscript += t;
        }
      }
      setLiveTranscript(interimTranscript || finalTranscript);

      if (finalTranscript.trim()) {
        setLiveTranscript('');
        if (autoListenRef.current) {
          handleVoiceSendRef.current?.(finalTranscript.trim());
        } else {
          setInput(finalTranscript.trim());
        }
      }
    };
    recognition.onerror = () => {
      setVoiceState(prev => prev === VOICE_STATE.LISTENING ? VOICE_STATE.IDLE : prev);
      setLiveTranscript('');
    };
    recognition.onend = () => {
      setVoiceState(prev => prev === VOICE_STATE.LISTENING ? VOICE_STATE.IDLE : prev);
      setLiveTranscript('');
    };
    recognitionRef.current = recognition;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Call mode controls ----

  const startCall = async () => {
    setInCall(true);
    setChatMode(false);
    autoListenRef.current = true;

    // Ensure session is created before starting
    if (!sessionTokenRef.current) {
      const result = await startSession();
      if (!result) return; // Session failed, bail out
    }

    // Speak the greeting if we have one
    if (lastBotText && ttsEnabledRef.current) {
      await playTTSRef.current?.(lastBotText);
    } else {
      // Start listening right away
      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
          setVoiceState(VOICE_STATE.LISTENING);
        } catch { /* */ }
      }
    }
  };

  const endCall = () => {
    setInCall(false);
    autoListenRef.current = false;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* */ }
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    // Clear sentence queue and stop browser speech
    speakSentenceQueue.current = [];
    isSpeakingQueue.current = false;
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setVoiceState(VOICE_STATE.IDLE);
    setLiveTranscript('');
  };

  const toggleMic = () => {
    if (!recognitionRef.current) return;
    if (voiceState === VOICE_STATE.LISTENING) {
      recognitionRef.current.stop();
      setVoiceState(VOICE_STATE.IDLE);
    } else if (voiceState === VOICE_STATE.IDLE || voiceState === VOICE_STATE.SPEAKING) {
      // Stop any playing audio and sentence queue (user is interrupting)
      if (audioRef.current) { audioRef.current.pause(); }
      speakSentenceQueue.current = [];
      isSpeakingQueue.current = false;
      if (typeof window !== 'undefined' && window.speechSynthesis) { window.speechSynthesis.cancel(); }
      setVoiceState(VOICE_STATE.IDLE);
      try {
        recognitionRef.current.start();
        setVoiceState(VOICE_STATE.LISTENING);
      } catch { /* */ }
    }
  };

  // Text chat send
  const handleTextSend = async () => {
    if (!input.trim() || sendingRef.current) return;
    const msg = input.trim();
    setInput('');
    sendingRef.current = true;
    setSending(true);
    const answer = await sendMessage(msg);
    sendingRef.current = false;
    setSending(false);
    if (answer && ttsEnabledRef.current && !chatMode && configRef.current?.conversation_mode !== 'chat_only') {
      playTTSRef.current?.(answer);
    }
  };

  const submitLead = async (form) => {
    const result = await startSession(form);
    if (result?.token) {
      fetch(`${API}/api/voice/public/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: result.token, visitor_name: form.name, visitor_email: form.email, visitor_phone: form.phone, visitor_company: form.company }),
      }).catch(() => {});
    }
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="voice-error-page">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Not Available</h1>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const bgStyle = config?.background_style === 'gradient'
    ? { background: config.background_value }
    : { backgroundColor: config?.background_value || '#FFFFFF' };

  const isVoiceCapable = config?.conversation_mode !== 'chat_only';
  const primaryColor = config?.primary_color || '#10B981';

  const stateLabel = {
    [VOICE_STATE.IDLE]: inCall ? 'Tap mic or just start speaking...' : '',
    [VOICE_STATE.LISTENING]: 'Listening...',
    [VOICE_STATE.THINKING]: `${config?.receptionist_name || 'AI'} is thinking...`,
    [VOICE_STATE.SPEAKING]: `${config?.receptionist_name || 'AI'} is speaking...`,
  };

  // ---- VOICE CALL VIEW (full-screen call experience) ----
  if (inCall && !chatMode) {
    return (
      <div className="min-h-screen flex flex-col" style={bgStyle} data-testid="public-voice-page">
        <style>{`
          @keyframes waveBar { 0% { transform: scaleY(0.3); } 100% { transform: scaleY(1); } }
          @keyframes pulseRing { 0% { transform: scale(1); opacity: 0.4; } 100% { transform: scale(1.5); opacity: 0; } }
        `}</style>

        {/* Minimal header */}
        <header className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {config?.logo_url && config?.show_logo && <img src={config.logo_url} alt="" className="h-6 w-auto" />}
            <span className="text-xs font-medium" style={{ color: config?.secondary_color }}>
              {config?.business_name || config?.receptionist_name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setTtsEnabled(v => !v)} className="p-1.5 rounded-full hover:bg-black/5 transition-colors" data-testid="tts-toggle-btn">
              {ttsEnabled ? <Volume2 className="h-4 w-4 text-gray-500" /> : <VolumeX className="h-4 w-4 text-gray-400" />}
            </button>
            <button onClick={() => { endCall(); setChatMode(true); }}
              className="px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-500 hover:bg-black/5 transition-colors"
              data-testid="switch-to-chat-btn">
              <MessageSquare className="h-3 w-3 inline mr-1" />Text
            </button>
          </div>
        </header>

        {/* Main call area */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          {/* Avatar with pulse ring */}
          <div className="relative mb-8">
            {voiceState === VOICE_STATE.SPEAKING && (
              <>
                <div className="absolute inset-0 rounded-full" style={{ backgroundColor: primaryColor + '30', animation: 'pulseRing 1.5s ease-out infinite' }} />
                <div className="absolute inset-0 rounded-full" style={{ backgroundColor: primaryColor + '20', animation: 'pulseRing 1.5s ease-out infinite 0.5s' }} />
              </>
            )}
            {voiceState === VOICE_STATE.LISTENING && (
              <div className="absolute inset-0 rounded-full" style={{ backgroundColor: '#EF444430', animation: 'pulseRing 1.5s ease-out infinite' }} />
            )}
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full flex items-center justify-center relative z-10 shadow-xl"
              style={{ backgroundColor: voiceState === VOICE_STATE.LISTENING ? '#FEE2E2' : primaryColor + '15' }}>
              {config?.avatar_url
                ? <img src={config.avatar_url} alt="AI" className="w-full h-full rounded-full object-cover" />
                : <Bot className="h-14 w-14 sm:h-16 sm:w-16" style={{ color: voiceState === VOICE_STATE.LISTENING ? '#EF4444' : primaryColor }} />}
            </div>
          </div>

          {/* Waveform */}
          <WaveformVisualizer state={voiceState} color={primaryColor} size="lg" />

          {/* Status label */}
          <p className="text-sm font-medium mt-4" style={{ color: config?.secondary_color }}>
            {stateLabel[voiceState]}
          </p>

          {/* Live transcript / last response */}
          <div className="mt-3 max-w-md text-center min-h-[60px]">
            {liveTranscript && (
              <p className="text-sm text-red-400 italic" data-testid="live-transcript">"{liveTranscript}"</p>
            )}
            {voiceState === VOICE_STATE.THINKING && (
              <div className="flex items-center justify-center gap-1.5 py-2">
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '300ms' }} />
              </div>
            )}
            {voiceState === VOICE_STATE.SPEAKING && lastBotText && (
              <p className="text-xs text-gray-400 line-clamp-3" data-testid="bot-transcript">"{lastBotText}"</p>
            )}
            {voiceState === VOICE_STATE.IDLE && !liveTranscript && lastBotText && !sending && (
              <p className="text-xs text-gray-300 line-clamp-2">Last: "{lastBotText.slice(0, 120)}{lastBotText.length > 120 ? '...' : ''}"</p>
            )}
          </div>
        </div>

        {/* Call controls */}
        <div className="flex items-center justify-center gap-6 pb-8 pt-4">
          {/* Mic toggle */}
          <button onClick={toggleMic}
            disabled={voiceState === VOICE_STATE.THINKING || voiceState === VOICE_STATE.SPEAKING}
            className={cn(
              "w-16 h-16 rounded-full flex items-center justify-center transition-all shadow-lg disabled:opacity-40",
              voiceState === VOICE_STATE.LISTENING ? "scale-110" : "hover:scale-105"
            )}
            style={{ backgroundColor: voiceState === VOICE_STATE.LISTENING ? '#EF4444' : primaryColor }}
            data-testid="call-mic-btn">
            {voiceState === VOICE_STATE.LISTENING
              ? <MicOff className="h-7 w-7 text-white" />
              : <Mic className="h-7 w-7 text-white" />}
          </button>

          {/* End call */}
          <button onClick={endCall}
            className="w-14 h-14 rounded-full flex items-center justify-center bg-red-500 hover:bg-red-600 transition-colors shadow-lg"
            data-testid="end-call-btn">
            <PhoneOff className="h-6 w-6 text-white" />
          </button>
        </div>

        {/* Tiny transcript history toggle */}
        {messages.length > 1 && (
          <div className="px-4 pb-4">
            <details className="text-center">
              <summary className="text-[10px] text-gray-300 cursor-pointer hover:text-gray-500 transition-colors">
                View conversation transcript ({messages.length} messages)
              </summary>
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5 text-left px-4">
                {messages.map((m, i) => (
                  <div key={i} className={cn("text-xs py-1 px-2 rounded", m.role === 'user' ? 'text-gray-600 bg-gray-50' : 'text-gray-500 bg-white')}>
                    <span className="font-medium">{m.role === 'user' ? 'You' : config?.receptionist_name}:</span> {m.content.slice(0, 200)}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        {config?.show_powered_by && (
          <footer className="text-center py-2 text-[9px] text-gray-200">Powered by Konnect</footer>
        )}
      </div>
    );
  }

  // ---- DEFAULT VIEW (pre-call landing / text chat) ----
  return (
    <div className="min-h-screen flex flex-col" style={bgStyle} data-testid="public-voice-page">
      <style>{`
        @keyframes waveBar { 0% { transform: scaleY(0.3); } 100% { transform: scaleY(1); } }
        @keyframes pulseRing { 0% { transform: scale(1); opacity: 0.4; } 100% { transform: scale(1.5); opacity: 0; } }
      `}</style>

      {/* Header */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="flex items-center gap-3">
          {config?.logo_url && config?.show_logo && <img src={config.logo_url} alt="Logo" className="h-8 w-auto" />}
          <div>
            <h1 className="text-sm font-semibold" style={{ color: config?.secondary_color }}>
              {config?.business_name || config?.receptionist_name}
            </h1>
            <p className="text-[11px] text-gray-400">AI Receptionist</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isVoiceCapable && (
            <button onClick={() => setTtsEnabled(v => !v)} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-testid="tts-toggle-btn">
              {ttsEnabled ? <Volume2 className="h-4 w-4 text-gray-600" /> : <VolumeX className="h-4 w-4 text-gray-400" />}
            </button>
          )}
          {config?.conversation_mode === 'voice_and_chat' && chatMode && (
            <button onClick={() => setChatMode(false)}
              className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              data-testid="switch-to-voice-btn">
              <Mic className="h-3.5 w-3.5 inline mr-1" />Voice
            </button>
          )}
        </div>
      </header>

      {/* Lead capture overlay */}
      {showLeadForm && (
        <div className="flex-1 flex items-center justify-center p-4">
          <LeadCaptureForm config={config} onSubmit={submitLead} onSkip={() => startSession()} />
        </div>
      )}

      {/* Main area */}
      {!showLeadForm && (
        <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full">
          {/* ---- Pre-call landing (before starting a voice call) ---- */}
          {!chatMode && isVoiceCapable && !inCall && (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <div className="w-28 h-28 rounded-full flex items-center justify-center mb-6 shadow-lg"
                style={{ backgroundColor: primaryColor + '15' }}>
                {config?.avatar_url
                  ? <img src={config.avatar_url} alt="AI" className="w-full h-full rounded-full object-cover" />
                  : <Bot className="h-12 w-12" style={{ color: primaryColor }} />}
              </div>

              <WaveformVisualizer state={VOICE_STATE.IDLE} color={primaryColor} size="lg" />

              <h2 className="text-xl sm:text-2xl font-semibold mt-4 mb-1" style={{ color: config?.secondary_color }}>
                {config?.welcome_headline || `Talk to ${config?.receptionist_name || 'our AI Assistant'}`}
              </h2>
              <p className="text-sm text-gray-500 max-w-md mb-8">
                {config?.welcome_subheadline || 'Start a voice conversation — just like calling us on the phone.'}
              </p>

              {/* Start Call Button */}
              <button onClick={startCall}
                className="w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-xl hover:scale-105 active:scale-95"
                style={{ backgroundColor: primaryColor }}
                data-testid="start-call-btn">
                <PhoneCall className="h-9 w-9 text-white" />
              </button>
              <p className="text-xs text-gray-400 mt-3">Tap to start a voice call</p>

              {/* Or switch to text */}
              {config?.conversation_mode === 'voice_and_chat' && (
                <button onClick={() => setChatMode(true)}
                  className="mt-6 text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1"
                  data-testid="prefer-text-btn">
                  <MessageSquare className="h-3 w-3" /> Prefer to type? Switch to text chat
                </button>
              )}
            </div>
          )}

          {/* ---- Text chat mode ---- */}
          {(chatMode || !isVoiceCapable) && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages-area">
                {messages.map((msg, i) => (
                  <ChatBubble key={i} msg={msg} config={config} />
                ))}
                {sending && (
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor + '20' }}>
                      <Bot className="h-3.5 w-3.5" style={{ color: primaryColor }} />
                    </div>
                    <div className="rounded-2xl px-4 py-3 bg-gray-100 rounded-tl-sm">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Text input */}
              {sessionToken && (
                <div className="p-3 sm:p-4 bg-white/80 backdrop-blur-md border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <input type="text" value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleTextSend()}
                      placeholder="Type your message..."
                      className="flex-1 px-4 py-2.5 rounded-full border border-gray-200 text-sm focus:outline-none focus:ring-2 bg-white"
                      disabled={sending}
                      data-testid="chat-input" />
                    <button onClick={handleTextSend}
                      disabled={!input.trim() || sending}
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-opacity disabled:opacity-40 flex-shrink-0"
                      style={{ backgroundColor: primaryColor }}
                      data-testid="chat-send-btn">
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* CTA Buttons */}
      {config?.cta_buttons?.length > 0 && !showLeadForm && (
        <div className="flex justify-center gap-3 px-4 py-2 flex-wrap">
          {config.cta_buttons.map((btn, i) => (
            <a key={i} href={btn.url} target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 rounded-full text-xs font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: primaryColor }}>
              {btn.icon === 'phone' && <Phone className="h-3 w-3 inline mr-1" />}
              {btn.icon === 'email' && <Mail className="h-3 w-3 inline mr-1" />}
              {btn.label}
            </a>
          ))}
        </div>
      )}

      {config?.show_powered_by && (
        <footer className="text-center py-3 text-[10px] text-gray-300">Powered by Konnect</footer>
      )}
    </div>
  );
}
